/**
 * WHICH BUILD of the Detective service (kamino) this browser talks to.
 *
 * Two Azure Container Apps run kamino against the SAME production database:
 * `main` and `staging`. Pointing the tab at staging is how a candidate build
 * gets exercised on a real opportunity, alongside production, so the two
 * reports can be read side by side — the only honest way to tell whether a
 * pipeline change moved the analysis rather than just the clock.
 *
 * Deliberately SEPARATE from the environment (production / staging DB) picker.
 * That one chooses which database kamino reads; this chooses which build reads
 * it. They used to be the same switch, which meant exercising a candidate
 * build also meant pointing it at staging data — and a comparison run needs
 * production data on both sides or it is not comparing anything.
 *
 * Module-level rather than Redux on purpose: `apiFetch` attaches the header for
 * every Detective call without ~20 call sites having to thread an option
 * through, and nothing else in the app needs to react to it.
 */
export type KaminoTarget = 'main' | 'staging';

const STORAGE_KEY = 'mavi.detective.kaminoTarget';

function readStored(): KaminoTarget {
  // Private windows and blocked site data both throw here; a failure to read a
  // preference must never stop the tab loading.
  try {
    return localStorage.getItem(STORAGE_KEY) === 'staging' ? 'staging' : 'main';
  } catch {
    return 'main';
  }
}

let current: KaminoTarget = readStored();

export function getKaminoTarget(): KaminoTarget {
  return current;
}

export function setKaminoTarget(target: KaminoTarget): void {
  current = target === 'staging' ? 'staging' : 'main';
  try {
    localStorage.setItem(STORAGE_KEY, current);
  } catch {
    /* preference is a nicety; the in-memory value still applies this session */
  }
}

/**
 * Only Detective paths carry the header. Everything else in tooling is served
 * in-process and has no notion of a kamino build, so tagging those requests
 * would be noise in the logs and a lie in the network tab.
 */
export function isDetectivePath(path: string): boolean {
  return path.includes('/deep-shortlist/');
}
